// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/AuditPage.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the
// row's `object_*` columns became the service's `target_*`; the predecessor's IP/agent
// columns became `origin` (this build's audit rides the platform envelope, which carries
// origin + request id rather than raw connection facts). The two readings and the
// row-click diff carry unchanged.
import { useQuery } from '@tanstack/react-query'
import { Fragment, useState } from 'react'

import { api } from '../../api/client'
import { auditSentence, dayOf } from '../../api/auditSentence'
import { Chip, type Tone } from '../../components/badges'
import { InfoTip } from '../../components/InfoTip'
import { TablePager } from '../../components/TablePager'

export interface AuditRow {
  id: string
  at: string | null
  client_id: string | null
  user_id: string | null
  user_name: string | null
  user_email: string | null
  actor_kind: string | null
  action: string
  target_kind: string
  target_id: string | null
  origin: string | null
  request_id: string | null
  before: unknown
  after: unknown
}

export interface AuditFilters {
  action?: string | null
  target_kind?: string | null
  target_id?: string | null
  from?: string | null
  to?: string | null
}

/** The query string for a set of filters.
 *
 *  Empty is not the same as unset: `action=` matches no action at all, so a
 *  cleared filter has to disappear from the URL rather than become blank. */
export function auditQuery(f: AuditFilters, page: number, size: number): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) {
    const s = (v ?? '').toString().trim()
    if (s) qs.set(k, s)
  }
  qs.set('page', String(page))
  qs.set('size', String(size))
  return qs.toString()
}

/** Colour by what the action DOES, not by its name. An audit reader scanning a
 *  page is looking for the things that changed data and the things that failed
 *  to authenticate; everything else is background. */
export function toneOf(action: string): Tone {
  // actions arrive namespaced (`resolution.approve`) — judge the verb tail
  const tail = action.slice(action.lastIndexOf('.') + 1)
  if (tail.endsWith('_failed') || tail.startsWith('login_failed')) return 'red'
  if (tail === 'login' || tail === 'logout' || tail === 'refresh') return 'gray'
  if (tail.startsWith('reverse') || tail.startsWith('retire')
    || tail.startsWith('delete')) return 'orange'
  if (tail.startsWith('approve') || tail.startsWith('reinstate')) return 'green'
  return 'blue'
}

/** A UUID reads as noise in a sentence; its 8-hex head is the same handle the
 *  run tags and export filenames use. Anything shorter passes through. */
function shortId(id: string | null): string | null {
  if (!id) return null
  return /^[0-9a-f]{8}-[0-9a-f]{4}/.test(id) ? id.slice(0, 8) : id
}

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''

const inputCls =
  'rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary'
const th = 'px-2 py-1.5 text-left text-xs font-medium uppercase text-muted-foreground'
const td = 'px-2 py-1.5'

export function AuditPage() {
  const [filters, setFilters] = useState<AuditFilters>({})
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(50)
  // the same rows, two readings — the grid for hunting a specific event,
  // "By day" for reading what happened. The raw before/after diff moved
  // behind a row click; it was the widest column and the rarest need.
  const [view, setView] = useState<'table' | 'day'>('table')
  const [open, setOpen] = useState<string | null>(null)
  const qs = auditQuery(filters, page, size)

  const q = useQuery({
    queryKey: ['audit', qs],
    queryFn: () => api<{ total: number; page: number; size: number; items: AuditRow[] }>(
      `/audit?${qs}`),
  })
  const actions = useQuery({
    queryKey: ['audit-actions'],
    queryFn: () => api<{ actions: string[] }>('/audit/actions'),
  })

  const set = (patch: AuditFilters) => {
    setFilters((f) => ({ ...f, ...patch }))
    setPage(1)
  }

  const sentence = (r: AuditRow) =>
    auditSentence({
      action: r.action,
      object_type: r.target_kind,
      object_id: shortId(r.target_id),
      user_name: r.user_name,
    })

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <h1 className="text-base font-semibold text-foreground">Audit trail</h1>
        <InfoTip>
          Every action that changed something, who did it and through what.
          Append-only: nothing in the app edits or deletes a row here.
        </InfoTip>
      </div>

      {q.isError ? (
        <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          Could not read the audit trail: {String(q.error)}
        </p>
      ) : (
        <>
          {/* the one-band rule: filters share the pager's row, above the data */}
          <TablePager total={q.data?.total ?? 0} page={page} size={size}
            onPage={setPage} onSize={setSize}
            middle={
              <div className="flex flex-wrap items-center gap-1.5">
                <select
                  className={`${inputCls} w-44`}
                  value={filters.action ?? ''}
                  onChange={(e) => set({ action: e.target.value || null })}
                >
                  <option value="">Any action</option>
                  {(actions.data?.actions ?? []).map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                <input className={`${inputCls} w-32`} placeholder="Target kind"
                  value={filters.target_kind ?? ''}
                  onChange={(e) => set({ target_kind: e.target.value })} />
                <input className={`${inputCls} w-24`} placeholder="Target id"
                  value={filters.target_id ?? ''}
                  onChange={(e) => set({ target_id: e.target.value })} />
                <input className={`${inputCls} w-32`} type="date"
                  value={filters.from ?? ''}
                  onChange={(e) => set({ from: e.target.value })} />
                <input className={`${inputCls} w-32`} type="date"
                  value={filters.to ?? ''}
                  onChange={(e) => set({ to: e.target.value })} />
                <span className="inline-flex overflow-hidden rounded-md border border-border text-xs">
                  {(['table', 'day'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`px-2 py-1 ${view === v ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                      onClick={() => setView(v)}
                    >
                      {v === 'table' ? 'Table' : 'By day'}
                    </button>
                  ))}
                </span>
                {q.isFetching && <span className="text-xs text-muted-foreground">…</span>}
              </div>
            } />
          {view === 'day' && (
            <div className="space-y-2">
              {[...new Set((q.data?.items ?? []).map((r) => dayOf(r.at)))].map((day) => (
                <div key={day} className="rounded-lg border border-border p-2.5">
                  <p className="mb-1 text-sm font-bold text-foreground">{day}</p>
                  <div className="space-y-0.5">
                    {(q.data?.items ?? []).filter((r) => dayOf(r.at) === day).map((r) => (
                      <div key={r.id} className="flex flex-nowrap items-center gap-2">
                        <span className="w-11 shrink-0 text-[10px] text-muted-foreground">
                          {(r.at ?? '').slice(11, 16)}
                        </span>
                        <Chip tone={toneOf(r.action)}>{r.action}</Chip>
                        <p className="min-w-0 truncate text-xs text-foreground">{sentence(r)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {(q.data?.items.length ?? 0) === 0 && !q.isPending && (
                <p className="text-sm text-muted-foreground">Nothing recorded for these filters.</p>
              )}
            </div>
          )}
          {view === 'table' && (
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[860px] text-xs">
                <thead>
                  <tr>
                    <th className={th} style={{ width: 150 }}>When</th>
                    <th className={th} style={{ width: 150 }}>Action</th>
                    <th className={th}>What happened</th>
                    <th className={th} style={{ width: 130 }}>Origin</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data?.items ?? []).map((r) => (
                    <Fragment key={r.id}>
                      <tr className="cursor-pointer border-t border-border"
                        onClick={() => setOpen(open === r.id ? null : r.id)}>
                        <td className={td}>{fmt(r.at)}</td>
                        <td className={td}><Chip tone={toneOf(r.action)}>{r.action}</Chip></td>
                        <td className={td}>
                          <p className="text-xs text-foreground">{sentence(r)}</p>
                          {r.user_email && (
                            <p className="text-[10px] text-muted-foreground">{r.user_email}</p>
                          )}
                        </td>
                        <td className={td}>
                          <span className="font-mono text-xs text-muted-foreground">{r.origin ?? '—'}</span>
                        </td>
                      </tr>
                      {open === r.id && (r.before != null || r.after != null) && (
                        <tr>
                          <td colSpan={4} className={`${td} bg-muted`}>
                            <pre className="whitespace-pre-wrap font-mono text-[10px] text-foreground">
                              {r.before != null
                                ? 'before: ' + JSON.stringify(r.before) + '\n' : ''}
                              {r.after != null
                                ? 'after:  ' + JSON.stringify(r.after) : ''}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                  {(q.data?.items.length ?? 0) === 0 && !q.isPending && (
                    <tr>
                      <td colSpan={4} className="p-4 text-sm text-muted-foreground">
                        Nothing recorded for these filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
